/**
 * src/components/spatial/SpatialObjectTooltip.tsx
 *
 * The hover card for a spatial object.
 *
 * Second consumer of `MapEntityTooltip`, and the reason it was
 * extracted: a visitor hovering the Burj Khalifa and a visitor hovering
 * the UAE should feel like they are using one map. So the chrome is the
 * country card's chrome, and only the contents differ.
 *
 * Visitor-facing and deliberately thin. No admin controls, no
 * beneficiary address, no registry state — those belong to the presence
 * editor, and putting them on a hover card would tell every passer-by
 * who can withdraw from a building. The beneficiary appears here as one
 * word, because whether a treasury has somewhere to pay out *to* is
 * something a would-be depositor should know.
 *
 * It renders. It does not fetch. Whatever treasury figure it shows was
 * loaded by the pinned card and handed down.
 */

import React from 'react';
import { MapEntityTooltip, tooltipBadgeStyle } from '../map/MapEntityTooltip';
import { eventStateLabel, type SpatialTooltipModel } from './spatialTooltipModel';

export interface SpatialObjectTooltipProps {
  model: SpatialTooltipModel;
  /** Cursor position in map-container pixels. */
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  /** Open a conversation. No wallet required — the server rations it instead. */
  onChat?: () => void;
  /** Listen to what the object is broadcasting. */
  onListen?: () => void;
  /** Deposit into the object's treasury. Requires a signature. */
  onDeposit?: () => void;
  /**
   * Open the event surface.
   *
   * Offered for an object that has an occasion on it, which the
   * tooltip model already knows from the derived event state — so
   * whether to show this is the same reading the map paints with,
   * rather than a second look at the metadata.
   */
  onEvent?: () => void;
  /** Open the full panel, which is where loading is allowed to happen. */
  onDetails?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const rowStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#64748b',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  flexShrink: 0,
};

const valueStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#e2e8f0',
  textAlign: 'right',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionStyle = (enabled: boolean): React.CSSProperties => ({
  flex: '1 1 0',
  padding: '6px 4px',
  borderRadius: '8px',
  border: 'none',
  background: enabled ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)',
  color: enabled ? '#818cf8' : '#475569',
  fontSize: '11px',
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed',
  transition: 'background 0.15s',
});

export const SpatialObjectTooltip: React.FC<SpatialObjectTooltipProps> = ({
  model,
  anchor,
  viewport = null,
  onChat,
  onListen,
  onDeposit,
  onEvent,
  onDetails,
  onMouseEnter,
  onMouseLeave,
}) => {
  const eventLabel = eventStateLabel(model.eventState);
  const treasury = model.treasury;

  // Offered when we cannot tell, refused only when we know. The server
  // decides either way; a card that hid Chat on a talkative building
  // would be the worse mistake.
  const chatEnabled = model.chat !== 'unavailable';
  // Same rule as Chat, and for the same reason. `unavailable` here means
  // we asked and there is nothing live; `unknown` means the active-
  // broadcast poll has not answered yet, and refusing on that would hide
  // a working player behind a timing accident.
  const listenEnabled = model.listen !== 'unavailable';
  const speaking = model.listen === 'available';
  const depositEnabled = !!onDeposit && treasury?.canReceive !== 'unavailable';

  /**
   * Absent and refused are different, and are shown differently.
   *
   * An action with no handler is not offered at all — a dead button is
   * a promise the application cannot keep. An action that exists but
   * does not apply to this object is shown greyed, because that says
   * something true about the object.
   */
  const action = (
    label: string,
    title: string,
    enabled: boolean,
    handler?: () => void,
  ) => {
    if (!handler) return null;
    return (
      <button
        type="button"
        title={title}
        disabled={!enabled}
        onClick={(e) => { e.stopPropagation(); handler(); }}
        style={actionStyle(enabled)}
      >
        {label}
      </button>
    );
  };

  return (
    <MapEntityTooltip
      data-testid="spatial-object-tooltip"
      anchor={anchor}
      viewport={viewport}
      // One hover card at a time, hence a fixed id. It outranks the
      // country card — both are hovers, but this one is over the thing
      // the spatial layer is actually about — and yields to everything
      // a click opened.
      surfaceId="object-tooltip"
      surfaceKind="object_tooltip"
      accentColor={model.accentColor}
      title={model.name}
      badge={<span style={tooltipBadgeStyle}>{model.typeLabel}</span>}
      estimatedHeight={220}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {model.place && (
        <div style={rowStyle}>
          <span style={labelStyle}>Where</span>
          <span style={valueStyle} title={model.place}>{model.place}</span>
        </div>
      )}

      {eventLabel && (
        <div style={rowStyle}>
          <span style={labelStyle}>Event</span>
          <span
            style={{
              ...valueStyle,
              color: model.eventState === 'live'
                ? '#4ade80'
                : model.eventState === 'soon' ? '#fbbf24' : '#94a3b8',
              fontWeight: 700,
            }}
          >
            {model.eventTitle ? `${model.eventTitle} · ${eventLabel}` : eventLabel}
          </span>
        </div>
      )}

      <div style={rowStyle}>
        <span style={labelStyle}>Talks</span>
        <span style={valueStyle}>
          {model.chat === 'available' ? 'Yes' : model.chat === 'unavailable' ? 'No' : '—'}
        </span>
      </div>

      {/* Only when it is true. A row saying "not broadcasting" on every
          quiet building would be noise, and quiet is the normal case. */}
      {speaking && (
        <div style={rowStyle}>
          <span style={labelStyle}>Audio</span>
          <span style={{ ...valueStyle, color: '#f87171', fontWeight: 700 }}>
            Speaking now
          </span>
        </div>
      )}

      {treasury && (
        <div style={rowStyle}>
          <span style={labelStyle}>Treasury</span>
          <span style={valueStyle}>
            {treasury.balance?.amount != null
              // Never a bare number: an amount without its asset is not
              // a balance.
              ? `${treasury.balance.amount} ${treasury.balance.assetCode || treasury.balance.asset}`
              : 'Not loaded'}
            {treasury.hasBeneficiary ? '' : ' · no payee'}
          </span>
        </div>
      )}

      <div style={{ padding: '8px 10px', display: 'flex', gap: '6px' }}>
        {action(
          'Chat',
          chatEnabled
            ? 'Ask this object a question — no wallet needed'
            : 'This object is not conversational',
          chatEnabled,
          onChat,
        )}
        {action(
          'Listen',
          speaking
            ? 'Listen to what this object is saying'
            : listenEnabled
              ? 'Check whether this object is broadcasting'
              : 'Nothing is being broadcast',
          listenEnabled,
          onListen,
        )}
        {action(
          'Deposit',
          depositEnabled
            ? 'Deposit into this object’s treasury — needs a signature'
            : 'This object cannot receive deposits',
          depositEnabled,
          onDeposit,
        )}
        {model.eventState && action(
          'Event',
          'What is on here, and what you can do',
          !!onEvent,
          onEvent,
        )}
        {action('Details', 'Open the full panel', true, onDetails)}
      </div>
    </MapEntityTooltip>
  );
};

export default SpatialObjectTooltip;
